import { useRef, useState, useEffect } from 'react';
import { Upload, FileText, X } from 'lucide-react';

export interface FileUploadAccept {
    mimeTypes?: string[];
    extensions?: string[];
}

function getFileKey(file: File): string {
    return `${file.name}-${file.size}-${file.lastModified}`;
}

const defaultValidate = (
    file: File,
    acceptMime?: string,
    maxBytes?: number
): { ok: boolean; message?: string } => {
    if (maxBytes != null && file.size > maxBytes) {
        return { ok: false, message: `File size must not exceed ${Math.round(maxBytes / 1024 / 1024)}MB` };
    }
    if (acceptMime) {
        const allowed = acceptMime.split(',').map(s => s.trim().toLowerCase());
        const isWildcard = allowed.some(a => a === '*' || a === '*/*');
        if (!isWildcard) {
            const mime = (file.type || '').toLowerCase();
            const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
            const byMime = allowed.some(a => a.startsWith('.') ? ext === a : a === mime);
            const byExt = allowed.some(a => a.startsWith('.') && ext === a);
            if (!byMime && !byExt) {
                return { ok: false, message: 'File type not allowed. Please check accepted formats.' };
            }
        }
    }
    return { ok: true };
};

type BaseProps = {
    label?: string;
    hint?: string;
    accept?: string;
    maxSizeBytes?: number;
    error?: string | null;
    disabled?: boolean;
    existingFileName?: string | null;
    existingFileDate?: string | Date | null;
    validate?: (file: File) => { ok: boolean; message?: string };
};

export type FileUploadProps = BaseProps & (
    | { multiple?: false; value: File | null; onChange: (file: File | null) => void }
    | { multiple: true; value: File[]; onChange: (files: File[]) => void }
);

export function FileUpload(props: FileUploadProps) {
    const {
        label,
        hint,
        accept,
        maxSizeBytes,
        error,
        disabled,
        existingFileName,
        existingFileDate,
        validate: customValidate,
    } = props;

    const inputRef = useRef<HTMLInputElement>(null);
    const [validationError, setValidationError] = useState<string | null>(null);
    const multiple = props.multiple === true;
    const value = props.value;
    const onChange = props.onChange;
    const multipleFileKeys = multiple ? ((value as File[] | null) || []).map(getFileKey).join(',') : '';

    // In multiple mode, keep preview URLs for images and revoke when files are removed
    const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});

    useEffect(() => {
        if (!multiple) return;
        const files = value as File[];
        const keys = new Set(files.map(getFileKey));
        setPreviewUrls(prev => {
            const next: Record<string, string> = {};
            files.forEach(file => {
                if (!file.type.startsWith('image/')) return;
                const key = getFileKey(file);
                next[key] = prev[key] ?? URL.createObjectURL(file);
            });
            Object.keys(prev).forEach(key => {
                if (!keys.has(key)) URL.revokeObjectURL(prev[key]);
            });
            return next;
        });
    }, [multiple, multipleFileKeys]);

    const runValidation = (file: File): { ok: boolean; message?: string } => {
        if (customValidate) return customValidate(file);
        return defaultValidate(file, accept, maxSizeBytes);
    };

    const displayError = validationError ?? error ?? null;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValidationError(null);
        const files = e.target.files;
        if (!files?.length) {
            if (!multiple) (onChange as (f: File | null) => void)(null);
            return;
        }
        const list = Array.from(files);
        if (multiple) {
            const next: File[] = [];
            for (const file of list) {
                const result = runValidation(file);
                if (!result.ok) {
                    setValidationError(result.message ?? 'Invalid file');
                    continue;
                }
                next.push(file);
            }
            if (next.length) {
                setValidationError(null);
                (onChange as (f: File[]) => void)([...(value as File[]), ...next]);
            }
        } else {
            const file = list[0];
            const result = runValidation(file);
            if (!result.ok) {
                setValidationError(result.message ?? 'Invalid file');
                (onChange as (f: File | null) => void)(null);
            } else {
                setValidationError(null);
                (onChange as (f: File | null) => void)(file);
            }
        }
        e.target.value = '';
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        setValidationError(null);
        if (disabled) return;
        const files = e.dataTransfer.files;
        if (!files?.length) return;
        const list = Array.from(files);
        if (multiple) {
            const next: File[] = [];
            for (const file of list) {
                const result = runValidation(file);
                if (!result.ok) {
                    setValidationError(result.message ?? 'Invalid file');
                    continue;
                }
                next.push(file);
            }
            if (next.length) {
                setValidationError(null);
                (onChange as (f: File[]) => void)([...(value as File[]), ...next]);
            }
        } else {
            const file = list[0];
            const result = runValidation(file);
            if (!result.ok) {
                setValidationError(result.message ?? 'Invalid file');
                return;
            }
            (onChange as (f: File | null) => void)(file);
        }
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        if (disabled) return;
    };

    const handleClick = () => {
        if (disabled) return;
        inputRef.current?.click();
    };

    const handleClearSingle = (e: React.MouseEvent) => {
        e.stopPropagation();
        setValidationError(null);
        (onChange as (f: File | null) => void)(null);
        if (inputRef.current) inputRef.current.value = '';
    };

    const handleRemoveAt = (e: React.MouseEvent, index: number) => {
        e.stopPropagation();
        setValidationError(null);
        const list = value as File[];
        const next = list.filter((_, i) => i !== index);
        (onChange as (f: File[]) => void)(next);
    };

    const existingDateStr = existingFileDate
        ? typeof existingFileDate === 'string'
            ? existingFileDate
            : new Date(existingFileDate).toLocaleDateString()
        : null;

    if (multiple) {
        const files = value as File[];
        return (
            <div className="w-full">
                {label && (
                    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                )}
                {hint && (
                    <p className="text-sm text-gray-500 mb-2">{hint}</p>
                )}
                <input
                    ref={inputRef}
                    type="file"
                    accept={accept}
                    multiple
                    onChange={handleInputChange}
                    className="hidden"
                    disabled={disabled}
                />
                <div
                    onClick={handleClick}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    className={`
                        min-h-[120px] rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 p-4
                        transition-colors cursor-pointer
                        ${displayError ? 'border-red-300 bg-red-50/50' : 'border-gray-300 bg-gray-50/80 hover:border-blue-400 hover:bg-blue-50/50'}
                        ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
                    `}
                >
                    <Upload className="h-10 w-10 text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-600">Click to choose or drag and drop (multiple)</span>
                    {files.length > 0 && (
                        <span className="text-xs text-gray-500">{files.length} file(s) selected</span>
                    )}
                </div>
                {files.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-3">
                        {files.map((file, index) => {
                            const key = getFileKey(file);
                            const url = file.type.startsWith('image/') ? previewUrls[key] : null;
                            return (
                                <div key={key} className="relative group">
                                    {url ? (
                                        <img
                                            src={url}
                                            alt={file.name}
                                            className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                                        />
                                    ) : (
                                        <div className="w-20 h-20 rounded-lg border border-gray-200 flex flex-col items-center justify-center bg-gray-50 p-1">
                                            <FileText className="h-8 w-8 text-gray-400" />
                                            <span className="text-xs text-gray-600 truncate w-full text-center">{file.name}</span>
                                        </div>
                                    )}
                                    <button
                                        type="button"
                                        onClick={(e) => handleRemoveAt(e, index)}
                                        className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-90 hover:opacity-100 text-xs"
                                        aria-label="Remove"
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                    <p className="text-xs text-gray-500 mt-0.5 truncate max-w-[80px]">{file.name}</p>
                                </div>
                            );
                        })}
                    </div>
                )}
                {displayError && (
                    <p className="mt-1 text-sm text-red-600">{displayError}</p>
                )}
            </div>
        );
    }

    const singleFile = value as File | null;
    return (
        <div className="w-full">
            {label && (
                <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
            )}
            {hint && (
                <p className="text-sm text-gray-500 mb-2">{hint}</p>
            )}
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                onChange={handleInputChange}
                className="hidden"
                disabled={disabled}
            />
            <div
                onClick={handleClick}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className={`
                    min-h-[120px] rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2
                    transition-colors cursor-pointer
                    ${displayError ? 'border-red-300 bg-red-50/50' : 'border-gray-300 bg-gray-50/80 hover:border-blue-400 hover:bg-blue-50/50'}
                    ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
                `}
            >
                {singleFile ? (
                    <>
                        <div className="flex items-center gap-2 text-gray-700">
                            <FileText className="h-8 w-8 text-blue-600 shrink-0" />
                            <span className="text-sm font-medium truncate max-w-[240px]">{singleFile.name}</span>
                            <span className="text-xs text-gray-500">
                                ({(singleFile.size / 1024).toFixed(1)} KB)
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={handleClearSingle}
                            disabled={disabled}
                            className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-100 px-2 py-1 rounded"
                        >
                            <X className="h-4 w-4" /> Remove
                        </button>
                    </>
                ) : (
                    <>
                        <Upload className="h-10 w-10 text-gray-400" />
                        <span className="text-sm text-gray-600">Click to choose or drag and drop</span>
                    </>
                )}
            </div>
            {displayError && (
                <p className="mt-1 text-sm text-red-600">{displayError}</p>
            )}
            {existingFileName && !singleFile && (
                <p className="mt-2 text-sm text-gray-500">
                    Uploaded: {existingFileName}
                    {existingDateStr && <span className="ml-2">({existingDateStr})</span>}
                </p>
            )}
        </div>
    );
}
